/**
 * useReadOnly Hook
 * 
 * Hook to check if current user has read-only access to an app.
 * Use canEdit to conditionally render action buttons (add, edit, delete).
 * 
 * @example
 * const { canEdit } = useReadOnly(APP_ID);
 * 
 * return (
 *   <>
 *     {canEdit && <Button>Add Item</Button>}
 *     {canEdit && <Button>Delete</Button>}
 *   </>
 * );
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiResponse } from '../services/api';

interface PermissionCheck {
  hasAccess: boolean;
  roleName: string | null;
  readOnly: boolean;
}

/**
 * Check if current user has read-only access to an app
 * @param appId - The app ID to check
 * @returns { isReadOnly, canEdit, isLoading }
 * 
 * Use `canEdit` to conditionally render buttons:
 * - If canEdit is TRUE - user can add/edit/delete - SHOW buttons
 * - If canEdit is FALSE - user is read-only - HIDE buttons
 */
export const useReadOnly = (appId: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['permissions', 'check', appId],
    queryFn: async (): Promise<PermissionCheck> => {
      const response = await apiClient.get<ApiResponse<PermissionCheck>>(
        `/permissions/check/${appId}`
      );
      return response.data.data;
    },
    enabled: !!appId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // While loading, default to showing buttons (canEdit = true)
  // After loaded, canEdit = !readOnly
  const isReadOnly = data?.readOnly ?? false;
  const canEdit = !isReadOnly;

  return {
    /** TRUE if user is read-only (cannot edit) */
    isReadOnly,
    /** TRUE if user can edit (not read-only) - use to show/hide action buttons */
    canEdit,
    /** TRUE if user has access to the app */
    hasAccess: data?.hasAccess ?? false,
    /** User's role name in this app */
    roleName: data?.roleName ?? null,
    /** TRUE while checking permissions */
    isLoading,
    /** Error if permission check failed */
    error,
  };
};

/**
 * Wrapper component to conditionally render children based on edit permissions
 * 
 * @example
 * <CanEdit appId={APP_ID}>
 *   <Button>Add Item</Button>
 *   <Button>Delete</Button>
 * </CanEdit>
 */
export const CanEdit: React.FC<{ appId: number; children: React.ReactNode }> = ({ appId, children }) => {
  const { canEdit } = useReadOnly(appId);
  return canEdit ? <>{children}</> : null;
};

export default useReadOnly;

